import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, SparklesIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { usePricing } from '../contexts/PricingContext';
import { ContactFormPopup } from './ContactFormPopup';
export function HeroVariant51() {
  const {
    language
  } = useLanguage();
  const {
    price
  } = usePricing();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const content = {
    ru: {
      badge: 'Новый подход к разработке',
      title1: 'Сделаем сайт для вас ',
      titleFree: 'бесплатно.',
      title2: `Обслуживание — ${price}/мес.`,
      sub: `Мы делаем сайт за свой счёт. Вы смотрите, просите правки, утверждаете. Когда всё нравится — платите ${price} в месяц за хостинг и поддержку. Не понравится — просто уходите. Без оплаты.`,
      cta: 'Получить бесплатный макет'
    },
    ua: {
      badge: 'Новий підхід до розробки',
      title1: 'Зробимо сайт для вас ',
      titleFree: 'безкоштовно.',
      title2: `Обслуговування — ${price}/міс.`,
      sub: `Ми робимо сайт за свій рахунок. Ви дивитеся, просите правки, затверджуєте. Коли все подобається — платите ${price} на місяць за хостинг та підтримку. Не сподобається — просто йдете. Без оплати.`,
      cta: 'Отримати безкоштовний макет'
    },
    en: {
      badge: 'A new approach to development',
      title1: 'We build your website ',
      titleFree: 'for free.',
      title2: `Maintenance — ${price}/mo.`,
      sub: `We build the site at our expense. You review, request edits, and approve. When you love it, you pay ${price}/month for hosting and support. Don't like it? Just walk away. No charge.`,
      cta: 'Get a free mockup'
    }
  }[language] || content.ru;
  return <>
      <section className="relative min-h-screen w-full overflow-hidden pt-32 pb-20 flex items-center" style={{
      backgroundColor: 'var(--bg-primary)'
    }} data-id="element-781">
        {/* Subtle background glow */}
        <div className="absolute top-1/4 left-0 w-[600px] h-[600px] bg-blue-400/10 rounded-full blur-3xl pointer-events-none" data-id="element-782" />

        <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-col lg:flex-row items-center justify-between gap-12 px-4" data-id="element-783">
          {/* Left Column - Premium SaaS Typography */}
          <div className="w-full lg:w-1/2 text-left flex flex-col items-start" data-id="element-784">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 text-blue-600 font-semibold text-sm mb-6 shadow-sm" data-id="element-785">
              <SparklesIcon className="w-4 h-4" data-id="element-786" />
              {content.badge}
            </motion.div>

            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="mb-4 text-4xl md:text-5xl lg:text-[3.5rem] font-black leading-[1.1] tracking-tight text-gray-900" data-id="element-787">
              {content.title1}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600" data-id="element-788">
                {content.titleFree}
              </span>
            </motion.h1>

            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="mb-8 text-2xl md:text-3xl font-bold text-gray-800" data-id="element-789">
              {content.title2}
            </motion.h2>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="text-lg md:text-xl mb-10 text-gray-600 font-medium max-w-lg leading-relaxed" data-id="element-790">
              {content.sub}
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} className="relative group" data-id="element-791">
              {/* Animated glow behind button */}
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200" data-id="element-792" />
              <button onClick={() => setIsPopupOpen(true)} className="relative flex items-center justify-center gap-2 rounded-2xl px-10 py-5 font-bold text-lg transition-all hover:scale-[1.02] bg-blue-600 text-white shadow-xl" data-id="element-793">
                {content.cta}
                <ArrowRightIcon className="w-5 h-5 transition-transform group-hover:translate-x-1" data-id="element-794" />
              </button>
            </motion.div>
          </div>

          {/* Right Column - Clean Video */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8
        }} className="w-full lg:w-1/2 relative mt-12 lg:mt-0" data-id="element-795">
            <div className="relative w-full max-w-2xl mx-auto rounded-3xl overflow-hidden shadow-2xl border border-gray-100" data-id="element-796">
              <video src="https://cdn.magicpatterns.com/uploads/vrXe21wkVqS62d5BzpZ9eJ/original-2de319f4767c5a0d4acf4f107b938504.mp4" autoPlay loop muted playsInline className="w-full h-auto" data-id="element-797" />
            </div>
          </motion.div>
        </div>
      </section>
      <ContactFormPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} data-id="element-798" />
    </>;
}